import type {AxiosError} from 'axios';
import {CompareEvents} from './compare-events';
import {
  DD_BROWSER_LOGGERS,
  DD_LOGS_CONTEXT_BY_LOGGER,
  FALLBACK_LOGS_MIDDLEWARE_ROUTE,
  WINDOW_LOGGER_GLOBAL_CONFIG_NAME,
} from './constants';
import {getLoggingEnabledSecondsForLowPrioResources} from './custom-sampling-low-prio-resources';
import {getTraceSpans} from './datadog/getTraceSpans';
import {DEFAULT_LOGGING_CONFIGURATION} from './default-logging-configuration';
import {
  LogLevel,
  LogOptions,
  LoggerWindow,
  LoggingConfigurationInterface,
  LoggingProviderInterface,
  PackageName,
  TadaridaWebVitals,
} from './types';
import {getMinimalAxiosErrorConfig} from './util/getMinimalAxiosErrorConfig';
// eslint-disable-next-line monorepo/no-internal-import
import {getIsConsentBannerAccepted} from '@aboutyou/storage/src/utils/consentBannerAcceptedState';
import {FallbackLog} from './fallbackLogsMiddleware/fallbackLogType';

declare let window: LoggerWindow;

/**
 * Logger module that is exported from this package.
 *
 * Purpose of this module is to keep track of situations inside the codebase, that may or may not signal a problem in the future
 * Moreover it is used to track generic error situation (like script errors)
 *
 * For monitoring specific events that are clearly distinguishable, consider using the monitor module
 *
 * The Logger can read configuration from global window object {@link LoggerWindow}
 * or get the configuration via public method {@link LoggingProviderInterface#withConfig}.
 *
 * @example
 *
 * ```
 * import {Log} from '@aboutyou/mlm';
 *
 * const loggerGlobalConfigScript = `<script>${generateLoggerGlobalConfig({
 *    console: true,
 * })}</script>`;
 * ```
 *
 * @example
 *
 * ```
 * import {Log} from '@aboutyou/mlm';
 *
 * Log.withConfig({
 *  console: true,
 *  datadogBrowser: false,
 * });
 *
 * const ReactElement = () => {
 *
 *   useEffect(() => {
 *      Log.info('package-name', 'ReactElement has rendered', {
 *        url: 'url',
 *        referrer: 'referrer',
 *      });
 *   }, []);
 *
 *   return (
 *     <div></div>
 *   );
 * };
 * ```
 *
 * @example
 *
 * The request parameters are added on window object as default parameters
 *
 * ```
 * import {Log} from '@aboutyou/mlm';
 *
 * const loggerGlobalConfigScript = `<script>${generateLoggerGlobalConfig({
 *   options: {
 *     url: "https://aboutyou.de",
 *     host: "prod-service",
 *     locale: "DE",
 *     abTests: {
 *       runningAbTests: [
 *         {
 *           name: "test-1",
 *           value: "test-value"
 *         }
 *       ]
 *     }
 *   }
 * })}</script>`;
 *
 * const ReactElement = () => {
 *
 *   useEffect(() => {
 *      Log.info('package-name', 'ReactElement has rendered');
 *   }, []);
 *
 *   return (
 *     <div></div>
 *   );
 * };
 * ```
 */
class Logger implements LoggingProviderInterface {
  private viewName?: string;
  private config: LoggingConfigurationInterface = {
    ...DEFAULT_LOGGING_CONFIGURATION,
  };

  private isWindowConfigSeeded = false;

  private secondsDuringWhichLoggingIsEnabledForLowPrioResources?: Set<number>;

  constructor(loggerConfig?: LoggingConfigurationInterface) {
    this.config = {...this.config, ...loggerConfig};
    this.init();
  }

  withWindowGlobalConfig(isManuallyUpdate?: boolean) {
    if (isManuallyUpdate || !this.isWindowConfigSeeded) {
      this.isWindowConfigSeeded = true;

      let loggerGlobalConfig = window[WINDOW_LOGGER_GLOBAL_CONFIG_NAME];

      if (loggerGlobalConfig && typeof loggerGlobalConfig === 'string') {
        loggerGlobalConfig = JSON.parse(loggerGlobalConfig);
      }

      if (loggerGlobalConfig) {
        this.config = {...this.config, ...loggerGlobalConfig};
        this.init();
      }
    }

    return this;
  }

  withConfig(updatedConfig: LoggingConfigurationInterface) {
    this.config = {...this.config, ...updatedConfig};
    this.init();
    return this;
  }

  getConfig() {
    return this.config;
  }

  public updateViewName(viewName: string) {
    this.viewName = viewName;
  }

  private shouldIgnoreTrace() {
    const currentSecond = new Date().getSeconds();
    return (
      this.secondsDuringWhichLoggingIsEnabledForLowPrioResources?.has(
        currentSecond,
      ) === false
    );
  }

  public setCustomAttribute(
    attributeName: string,
    attributeValue: string | number,
  ) {
    if (process.browser) {
      if (this.config.datadogBrowser) {
        window.DD_LOGS?.onReady(function () {
          window.DD_LOGS?.setGlobalContextProperty?.(
            attributeName,
            attributeValue,
          );
        });
      }
    }
  }

  public addAttributeToTransaction(
    attributeName: string,
    attributeValue: string | number | boolean | Error,
  ) {
    if (!process.browser && this.config.datadogTracer) {
      const {rootSpan} = getTraceSpans(this.config.datadogTracer);
      rootSpan?.setTag(attributeName, attributeValue);
    }
  }

  private addAttributesToTransaction(
    attributes: Array<[string, string | number | boolean]>,
  ) {
    if (!process.browser && this.config.datadogTracer) {
      const {rootSpan} = getTraceSpans(this.config.datadogTracer);
      for (const [attributeName, attributeValue] of attributes) {
        rootSpan?.setTag(attributeName, attributeValue);
      }
    }
  }

  private addErrorToTransaction(
    spanName: string,
    error: Error,
    options: {[key: string]: string | number | boolean} = {},
  ) {
    if (!process.browser && this.config.datadogTracer) {
      const {rootSpan} = getTraceSpans(this.config.datadogTracer);
      rootSpan?.setTag(spanName, error);

      for (const [attributeName, attributeValue] of Object.entries(options)) {
        rootSpan?.setTag(`${spanName}.${attributeName}`, attributeValue);
      }
    }
  }

  private logByBrowserLogger(
    config: Parameters<typeof this.logWithFallbackLogger>[0],
  ) {
    try {
      if (getIsConsentBannerAccepted()) {
        window.DD_LOGS?.onReady(function () {
          const logger = window.DD_LOGS?.getLogger?.(config.level);
          if (config.level === DD_BROWSER_LOGGERS.CUSTOM_ERROR_LOGS) {
            logger?.error(
              config.message,
              {
                ...config.options,
                online: window.navigator?.onLine ?? 'unknown',
              },
              config.error,
            );
          } else {
            logger?.log(config.message, {
              ...config.options,
              online: window.navigator?.onLine ?? 'unknown',
            });
          }
        });
      } else {
        this.logWithFallbackLogger(config);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Cannot send error to datadog LOGS', err);
    }
  }

  public logWithFallbackLogger(
    config: {
      message: string;
      options: Record<string, any>;
    } & (
      | {
          level:
            | DD_BROWSER_LOGGERS.CRITICAL_LOGS
            | DD_BROWSER_LOGGERS.NON_CRITICAL_LOGS
            | DD_BROWSER_LOGGERS.WEB_VITALS;
        }
      | {
          level:
            | DD_BROWSER_LOGGERS.CUSTOM_ERROR_LOGS
            | DD_BROWSER_LOGGERS.WARNING_LOGS
            | DD_BROWSER_LOGGERS.UNHANDLED_ERROR_LOGS
            | DD_BROWSER_LOGGERS.DEBUG_LOGS;
          error: Error;
        }
    ),
  ) {
    if (this.shouldIgnoreTrace()) {
      return;
    }

    try {
      const payload: Partial<FallbackLog> = {
        device: process.env.STACK,
        ddtags: [
          `env:${this.config.logContext?.env}`,
          `version:${this.config.logContext?.version}`,
        ].join(','),
        message: config.message,
        level: config.level,
        logger: {name: config.level},
        env: this.config.logContext?.env,
        landingPageType: this.config.logContext?.landingPageType,
        pageType: this.config.logContext?.landingPageType || this.viewName,
        view: {
          url_details: {
            host: this.config.logContext?.hostname,
          },
        },
        online: window.navigator?.onLine ?? 'unknown',
        ...DD_LOGS_CONTEXT_BY_LOGGER[config.level],
        ...config.options,
        ...('error' in config
          ? {
              error: {
                message: config.error.message,
                stack: config.error.stack,
                name: config.error.name,
                code: (config.error as any).code,
              },
            }
          : {}),
      };

      fetch(FALLBACK_LOGS_MIDDLEWARE_ROUTE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Cannot send error to datadog LOGS', err);
    }
  }

  private logBrowserError(baseError: ReturnType<typeof this.getErrorBase>) {
    if (!baseError) {
      return;
    }

    try {
      this.logByBrowserLogger({
        level: DD_BROWSER_LOGGERS.CUSTOM_ERROR_LOGS,
        message: baseError.errorObject.message,
        options: baseError.options,
        error: baseError.errorObject,
      });
    } catch (err) {
      console.warn('Cannot send error to datadog RUM', err);
    }
  }

  /**
   * This method logs the message & payload with status `info`.
   * This can be useful to log a message/payload in production which can be also used to create custom dashboards/alerts in datadog.
   *
   * `Client-side`: (low sample rate)
   * - The logs are sent to datadog logs (using DD_LOGS)
   * - console.log in dev mode only
   *
   * `Server-side`:
   * - Does not send the log to Datadog (since we don't have logs enabled for the server currently)
   * - console.log in dev mode only
   */
  public info(packageName: PackageName, message: string, data?: LogOptions) {
    const options = {...(this.config.options || {}), ...data};

    if (!this.isLogLevelActive(LogLevel.INFO)) {
      return;
    }

    const formattedMessage = this.formatMessageWithPackageName(
      packageName,
      message,
    );

    this.consoleLogByMethod({
      consoleLevel: 'info',
      message: formattedMessage,
      data: options,
    });

    if (process.browser && this.config.datadogBrowser) {
      this.logByBrowserLogger({
        level: DD_BROWSER_LOGGERS.NON_CRITICAL_LOGS,
        message: formattedMessage,
        options,
      });
    }
  }

  public getErrorBase(
    packageName: PackageName,
    message: string | Error,
    data: LogOptions = {},
  ) {
    const {error, ...additionalOptions} = data;
    const collectedOptions: {[key: string]: any} = {packageName};

    // collect all error properties (like message, stack, code, metadata etc.) and add them to the log
    // The error `stack` property is already part of the error object and thus not needed in the log since having it in the logs
    // would only increase the size of the log and thus the costs
    if (error) {
      Object.entries(error).forEach(([key, value]) => {
        if ((message as AxiosError).isAxiosError && key === 'config') {
          collectedOptions.error_config = getMinimalAxiosErrorConfig(
            message as AxiosError,
          );
          return;
        }

        collectedOptions[`error_${key}`] = value;
      });
      if (error.message) {
        collectedOptions.error_message = error.message;
      }
    }
    if (message instanceof Error) {
      Object.entries(message).forEach(([key, value]) => {
        if ((message as AxiosError).isAxiosError && key === 'config') {
          collectedOptions.error_message_config = getMinimalAxiosErrorConfig(
            message as AxiosError,
          );
          return;
        }

        collectedOptions[`error_message_${key}`] = value;
        collectedOptions.error_message_message = message.message;
      });
    }

    const options = Object.assign(
      collectedOptions,
      this.config.options,
      additionalOptions,
    );

    const formattedMessage = this.formatMessageWithPackageName(
      packageName,
      message instanceof Error ? message.message : message,
    );
    collectedOptions.message = formattedMessage;

    const errorObject = new Error(formattedMessage);
    if (message instanceof Error && message.stack) {
      errorObject.stack = message.stack;
    } else if (error && error.stack) {
      errorObject.stack = error.stack;
    }

    return {errorObject, options};
  }

  /**
   * This method logs the message & payload with status `warn`.
   * This can be used to log non-critical errors to datadog. This normally wont create an alert in datadog.
   *
   * `Client-side`: (very low sample rate)
   * - The logs are sent to datadog logs (using DD_LOGS)
   * - console.warn in dev mode only
   *
   * `Server-side`:
   * - Does not send the log to Datadog (since we don't have logs enabled for the server currently)
   * - console.warn in dev mode only
   */
  public warn(
    packageName: PackageName,
    message: string,
    data: LogOptions = {},
  ) {
    if (!this.isLogLevelActive(LogLevel.WARN)) {
      return null;
    }

    const baseError = this.getErrorBase(packageName, message, data);

    this.consoleLogByMethod({
      consoleLevel: 'warn',
      message: baseError.errorObject,
      data: baseError.options,
    });

    if (process.browser && this.config.datadogBrowser) {
      this.logByBrowserLogger({
        level: DD_BROWSER_LOGGERS.WARNING_LOGS,
        message: baseError.errorObject.message,
        options: baseError.options,
        error: baseError.errorObject,
      });
    }
  }

  /**
   * This method logs the message & payload with status `warn`.
   * This can be useful for debugging production issues. These logs are not indexed by datadog and thus cannot be used to create custom dashboards/alerts in datadog.
   * However, these logs can be used to debug production issues by temporarily enabling the debugs logs in datadog index,
   * from here: https://app.datadoghq.eu/logs/pipelines/indexes
   *
   * `Client-side`:
   * - The logs are sent to datadog logs but not indexed/retained (using DD_LOGS)
   * - console.debug in dev mode only
   *
   * `Server-side`:
   * - Does not send the log to Datadog (since we don't have logs enabled for the server currently)
   * - console.debug in dev mode only
   */
  public debug(
    packageName: PackageName,
    message: string,
    data: LogOptions = {},
  ) {
    if (!this.isLogLevelActive(LogLevel.DEBUG)) {
      return;
    }

    const baseError = this.getErrorBase(packageName, message, data);

    this.consoleLogByMethod({
      consoleLevel: 'debug',
      message: baseError.errorObject,
      data: baseError.options,
    });

    if (process.browser && this.config.datadogBrowser) {
      this.logByBrowserLogger({
        level: DD_BROWSER_LOGGERS.DEBUG_LOGS,
        message: baseError.errorObject.message,
        options: baseError.options,
        error: baseError.errorObject,
      });
    }
  }

  /**
   * This method logs the error message & object with status `error`. Use this in case of critical errors that are critical for monitoring.
   * These error logs can create an alert in datadog when the error rate is high.
   *
   * `Client-side`: (medium sample rate)
   * - The logs are sent to datadog logs (using DD_LOGS)
   * - console.error in dev mode only
   *
   * `Server-side`:
   * - The error log is added to the datadog server trace (using dd tracer)
   * - console.error in dev mode only
   */
  public error(
    packageName: PackageName,
    message: string | Error,
    data: LogOptions = {},
  ) {
    if (!this.isLogLevelActive(LogLevel.ERROR)) {
      return null;
    }
    const baseError = this.getErrorBase(packageName, message, data);

    this.consoleLogByMethod({
      consoleLevel: 'error',
      message: baseError.errorObject,
      data: baseError.options,
    });

    if (process.browser && this.config.datadogBrowser) {
      this.logBrowserError(baseError);
      return;
    }

    this.addErrorToTransaction(
      'error',
      baseError.errorObject,
      baseError.options,
    );
  }

  /**
   * This method logs the compare event to datadog browser logs (using DD_LOGS)
   * The logs collected from these events can be used to create Success Rate Comparison dashboards and alerts in datadog
   *
   * `Client-side`: (high sample rate)
   * - The logs are sent to datadog logs (using DD_LOGS)
   * - console.log in dev mode only
   *
   * `Server-side`:
   * - Does nothing
   * - console.log in dev mode only
   */
  public compare<ActionName extends keyof CompareEvents>(config: {
    actionName: ActionName;
    attributes: CompareEvents[ActionName];
    success: boolean;
  }) {
    const options = {
      ...config.attributes,
      action: config.actionName,
      success: config.success,
    };
    this.consoleLogByMethod({
      consoleLevel: 'info',
      message: config.actionName,
      data: options,
    });

    if (process.browser && this.config.datadogBrowser) {
      this.logByBrowserLogger({
        level: DD_BROWSER_LOGGERS.CRITICAL_LOGS,
        message: config.actionName,
        options,
      });
    }
  }

  /**
   * This method logs the core web-vitals with status `info`.
   */
  public reportWebVitals(message: TadaridaWebVitals, data?: LogOptions) {
    const options = {...(this.config.options || {}), ...data};

    if (!this.isLogLevelActive(LogLevel.INFO)) {
      return;
    }

    const formattedMessage = `WebVitals: ${message}`;
    const metricValue = (data as any)?.metric_value;
    const metricScore = (data as any)?.metric_score;
    const value = metricScore ?? metricValue;

    this.consoleLogByMethod({
      consoleLevel: 'info',
      message: [`🔵 ${formattedMessage}:`, value],
      data: options,
      collapsed: true,
    });

    if (process.browser && this.config.datadogBrowser) {
      this.logByBrowserLogger({
        level: DD_BROWSER_LOGGERS.WEB_VITALS,
        message: formattedMessage,
        options: {
          ...options,
          action: formattedMessage,
        },
      });
    }
  }

  public transact(
    name: string,
    options: {[key: string]: string | boolean},
    config?: {limitTracesIngested?: boolean},
  ) {
    if (!process.browser && this.config.datadogTracer) {
      this.addAttributeToTransaction('resource.name', name);

      // datadog makes it possible to ignore traces based of custom tags
      // https://docs.datadoghq.com/tracing/guide/ignoring_apm_resources/?tab=datadogyaml#ignoring-based-on-span-tags
      // we use this to ignore traces that are not important for us by setting the tag `ignore_trace` to `true`
      // the ignore_trace tag is configured in the datadog-agent cloudformation template
      if (config?.limitTracesIngested && this.shouldIgnoreTrace()) {
        this.addAttributeToTransaction('ignore_trace', 'true');
      }

      this.addAttributesToTransaction(Object.entries(options));
    }
  }

  public nonCriticalServerError(
    packageName: PackageName,
    message: string | Error,
    data: LogOptions = {},
  ) {
    if (!this.isLogLevelActive(LogLevel.ERROR)) {
      return null;
    }
    const baseError = this.getErrorBase(packageName, message, data);

    this.consoleLogByMethod({
      consoleLevel: 'error',
      message: baseError.errorObject,
      data: baseError.options,
    });

    this.addErrorToTransaction(
      'nonCriticalError',
      baseError.errorObject,
      baseError.options,
    );
  }

  private init() {
    if (this.config.samplingRateForLowPrioResources) {
      this.secondsDuringWhichLoggingIsEnabledForLowPrioResources =
        getLoggingEnabledSecondsForLowPrioResources({
          sampleRate: this.config.samplingRateForLowPrioResources,
        });
    }
  }

  private isLogLevelActive(logLevel: LogLevel): boolean {
    if (this.config.level === undefined) {
      return false;
    }
    return logLevel.valueOf() >= this.config.level.valueOf();
  }

  private consoleLogByMethod({
    consoleLevel,
    message,
    ...rest
  }: {
    consoleLevel: 'error' | 'warn' | 'info' | 'debug';
    message: string | string[] | Error;
    data?: {};
    collapsed?: boolean;
  }) {
    const {collapsed = false, data = {}} = rest;
    if (this.config.console) {
      const _message = Array.isArray(message) ? message : [message];
      if (collapsed) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(..._message);
        // eslint-disable-next-line no-console
        console[consoleLevel](data);
        // eslint-disable-next-line no-console
        console.groupEnd();
      } else {
        // eslint-disable-next-line no-console
        console[consoleLevel](..._message, data);
      }
    }
  }

  private formatMessageWithPackageName(packageName: string, message: string) {
    return `[${packageName}] ${message}`;
  }
}

export const Log = new Logger();

if (typeof window !== 'undefined') {
  Log.withWindowGlobalConfig();
}
